import React, { useEffect, useState } from 'react'
// @ts-ignore
import { meters2ScreenPixels } from 'google-map-react/utils'
import Loader from 'react-loader-spinner'

import Header from '../../components/Header'
import MapSection from './components/MapSection'
import InfoSection from './components/InfoSection'
import ValueSection from './components/ValueSection'
import AnimalsSection from './components/AnimalsSection'
import PropertyTypeSection from './components/PropertyTypeSection'
import AccordionsSection from './components/AccordionsSection'

import './affiliates.scss'
import COLORS from '../../styles/colors'

import { FilterState } from '../../types/filter'
import propertyDetails from '../../constants/property-details'
import condominiumDetails from '../../constants/condominium-details'
import SignupForm from '../../components/SignupForm'
import createUser from '../../services/create-user'
import saveFilter from '../../services/save-filter'
import { authenticateUser, authWithFacebook } from '../../services/auth'
import StoreButtons from '../../components/StoreButtons'
import useFacebook from '../../custom-hooks/useFacebook'
import { filterString } from 'helpers/cleanup'

const initialState: FilterState = {
  distance: 10,
  valueType: 'rent',
  valueInterval: [200, 20000],
  areaInterval: [10, 500],
  propertyTypes: ['apartment'],
  circleCenter: {
    latitude: 0,
    longitude: 0,
  },
  details: {},
  condominium_details: {},
  info: {},
}

const Affiliates = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const reference = urlParams.get('ref')

  // Redirect page: https://mellro.atlassian.net/browse/ML-7090
  useEffect(() => {
    window.location.href = 'https://mellro.com/em-reforma/'
  }, [])

  const [state, setState] = useState(initialState)
  const [mapZoom, setMapZoom] = useState(10)
  const [circleRadius, setCircleRadius] = useState(
    meters2ScreenPixels(
      state.distance * 1000,
      { lat: state.circleCenter.latitude, lng: state.circleCenter.longitude },
      mapZoom,
    ),
  )

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
  })

  const [isLoading, setIsLoading] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [error, setError] = useState('')

  const { fbSdkLoaded } = useFacebook()

  const onSubmit = async () => {
    // @ts-ignore
    if (ga && typeof ga === 'function') {
      // @ts-ignore
      ga('send', 'event', 'botao', 'clique', 'filtros')
    }
    if (reference) {
      setIsLoading(true)
      setError('')

      const filteredPhone = filterString(form.phone, ['(', ')', '+', '-', ' '])

      try {
        const user = await createUser({
          ...form,
          phone: filteredPhone,
          reference,
        })
        const auth = await authenticateUser(form.email, form.password)
        await saveFilter(user.id, auth.accessToken, state)
        setHasSubmitted(true)
      } catch (error) {
        console.log(error.data)
        setError(`Não foi possível realizar o seu cadastro e salvar
        os seus filtros no momento. Tente novamente mais tarde.
        Detalhes técnicos: ${error?.details}`)
      } finally {
        setIsLoading(false)
      }
    }
  }

  const updateField = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    })
  }

  const changeMapCircleSize = (center = state.circleCenter, zoom = mapZoom) => {
    const { w } = meters2ScreenPixels(
      state.distance * 1000,
      { lat: center.latitude, lng: center.longitude },
      zoom,
    )
    setCircleRadius(w)
  }

  const setCircleCenter = (lat, lng) => {
    setState({
      ...state,
      circleCenter: {
        latitude: lat,
        longitude: lng,
      },
    })
  }

  const setDistance = (value) => {
    setState({ ...state, distance: value })
  }

  const setValueInterval = (value: number[]) => {
    setState({ ...state, valueInterval: value })
  }

  const setValueType = (valueType: string) => {
    setState({ ...state, valueType: valueType })
  }

  const setPropertyType = (propertyType: string) => {
    let selectedPropertyTypes: string[]
    if (state.propertyTypes.includes(propertyType)) {
      selectedPropertyTypes = state.propertyTypes.filter((type) => type !== propertyType)
    } else {
      selectedPropertyTypes = [...state.propertyTypes, propertyType]
    }
    setState({ ...state, propertyTypes: [...selectedPropertyTypes] })
  }

  const setAreaInterval = (area: number[]) => {
    setState({ ...state, areaInterval: area })
  }

  const setInfo = (info: string, value: number | undefined) => {
    setState({
      ...state,
      info: {
        ...state.info,
        [info]: value,
      },
    })
  }

  const setPetFriendlyDetail = (value) => {
    setState({
      ...state,
      details: {
        ...state.details,
        pet_friendly: value,
      },
    })
  }

  const setDetails = (detailType: string, detail: string) => {
    let currentDetails = state[detailType]

    if (Boolean(currentDetails[detail])) {
      // eslint-disable-next-line
      const { [detail]: _, ...rest } = currentDetails
      currentDetails = rest
    } else {
      currentDetails = { ...currentDetails, [detail]: true }
    }

    setState({
      ...state,
      [detailType]: {
        ...currentDetails,
      },
    })
  }

  const processFacebookData = async (accessToken: string) => {
    setIsLoading(true)
    setError('')
    try {
      const { data } = await authWithFacebook(accessToken)
      if (data.user) {
        await saveFilter(data.user.id, data.access_token, state)
        setHasSubmitted(true)
      }
    } catch (error) {
      console.log('error', { error })
      setError(`Não foi possível realizar o seu cadastro e salvar
      os seus filtros no momento. Tente novamente mais tarde.
      Detalhes técnicos: ${error?.details}`)
    } finally {
      setIsLoading(false)
    }
  }

  const onClickFacebook = (e) => {
    e.preventDefault()
    // @ts-ignore
    window.FB.login(
      (response) => {
        if (response.status === 'connected') {
          // Logged into your webpage and Facebook.
          processFacebookData(response.authResponse.accessToken)
        } else {
          // The person is not logged into your webpage or we are unable to tell.
          console.log("couldn't log in with facebook")
        }
      },
      { scope: 'public_profile,email' },
    )
  }

  // Show nothing until page is redirected
  return null

  // eslint-disable-next-line no-unreachable
  return (
    <div>
      <Header textComponent={<p className='header__text'>Encontre um lugar para chamar de seu!</p>} />
      <div className='intro'>
        <h1 className='intro__headline'>Busque seu imóvel</h1>
        <p className='intro__subheadline'>
          Encontre o imóvel de seus sonhos direto com o dono, sem imobiliária, com agilidade e sem burocracia.
        </p>
      </div>
      <MapSection
        circleCenter={state.circleCenter}
        setCircleCenter={setCircleCenter}
        setDistance={setDistance}
        distance={state.distance}
        circleRadius={circleRadius}
        changeMapCircleSize={changeMapCircleSize}
        setMapZoom={setMapZoom}
      />
      <ValueSection
        setValueInterval={setValueInterval}
        onSelectOption={setValueType}
        selected={state.valueType}
        valueInterval={state.valueInterval}
      />
      <PropertyTypeSection setPropertyType={setPropertyType} propertyTypes={state.propertyTypes} />
      <InfoSection
        areaInterval={state.areaInterval}
        setInfoValue={setInfo}
        details={state.info}
        setAreaInterval={setAreaInterval}
      />
      <AnimalsSection onSelectOption={setPetFriendlyDetail} selected={Boolean(state.details.pet_friendly)} />
      <AccordionsSection
        propertyDetails={state.details}
        propertyDetailsOptions={propertyDetails}
        condDetailsOptions={condominiumDetails}
        condDetails={state.condominium_details}
        setDetails={setDetails}
      />
      {fbSdkLoaded ? (
        <SignupForm
          isLoading={isLoading}
          hasSubmitted={hasSubmitted}
          form={form}
          onSubmit={onSubmit}
          updateField={updateField}
          headline={'COMPLETE SEU CADASTRO'}
          subheadline={'e salve sua busca personalizada em nosso App!'}
          submitButtonText={'Receber imóveis direto com o dono'}
          onClickFacebook={onClickFacebook}
          sucessMessage={
            'Parabéns, sua conta foi criada e seus filtros foram salvos! Entre no nosso aplicativo para conferir!'
          }
        />
      ) : (
        <Loader
          type='Oval'
          color='#00BFFF'
          height={60}
          width={60}
          style={{ margin: '50px auto', display: 'flex', justifyContent: 'center' }}
        />
      )}
      {Boolean(error) && <p style={{ color: 'red', textAlign: 'center', margin: 16 }}>{error}</p>}
      {hasSubmitted && <StoreButtons customContainerStyle={{ backgrsoundColor: COLORS.lightWhite }} />}
      <div style={affiliatesStyles.footer}>
        <div style={affiliatesStyles.footerSection}>
          <p style={affiliatesStyles.footerLabel}>VAMOS CONVERSAR?</p>
          <p style={affiliatesStyles.smallText}>contato@mellro.com</p>
          <p style={affiliatesStyles.smallText}>(41) 98788-9368</p>
        </div>
        <div style={affiliatesStyles.footerSection}>
          <p style={affiliatesStyles.footerLabel}>ONDE ESTAMOS:</p>
          <p style={affiliatesStyles.smallText}>Fazemos o Mellro com carinho em Curitiba</p>
        </div>
        <p style={affiliatesStyles.copyright}>© 2020 Mellro All Rights Reserved</p>
      </div>
    </div>
  )
}

export const affiliatesStyles: { [name: string]: React.CSSProperties } = {
  sectionTitle: {
    color: COLORS.greyscale.black,
    fontWeight: 800,
    fontSize: 18,
    textAlign: 'center',
    marginBottom: 16,
  },
  centerLabel: {
    color: COLORS.greyscale.black,
    fontSize: 16,
    lineHeight: '19px',
    textAlign: 'center',
  },
  slider: {
    margin: '15px auto',
    width: '95%',
  },
  trackStyle: {
    backgroundColor: COLORS.brand.primaryDark,
    height: 10,
  },
  handleStyle: {
    borderColor: COLORS.greyscale.dark,
    height: 25,
    width: 25,
    marginTop: -9,
    backgroundColor: COLORS.greyscale.white,
  },
  railStyle: {
    backgroundColor: COLORS.lightGray,
    height: 10,
  },
  footer: {
    padding: '20px 16px',
  },
  footerSection: {
    marginBottom: 16,
  },
  footerLabel: {
    fontWeight: 700,
    color: COLORS.greyscale.black,
    fontSize: 12,
  },
  smallText: {
    color: COLORS.greyscale.black,
    fontSize: 12,
    lineHeight: '19px',
  },
  copyright: {
    color: COLORS.greyscale.black,
    fontSize: 12,
    paddingTop: 16,
    borderTop: `1px solid ${COLORS.lightWhite}`,
  },
}

export default Affiliates
